/**
 * Central helper for post-login return-to routing.
 *
 * Rules (per product spec):
 *   • Any click that sends the user to /login or /register from a browsing
 *     surface (class, package, event, schedule, booking, checkout, coach page…)
 *     MUST call `saveReturnTo()` first so that after login we can restore the
 *     exact URL — full pathname + search params.
 *   • After successful login (email/password, Google, or Register), call
 *     `consumeReturnTo()` to read + clear the saved path and navigate there.
 *     If none is saved, fall back to Home (`/`) — never /dashboard.
 *   • Never save auth-flow paths (`/login`, `/register`, `/auth-callback`) as
 *     the return target — that would trap users in a loop.
 */

const STORAGE_KEY = 'returnTo';
// Kept for backwards compat with the older Google flow (`postLoginRedirect`).
// Reads fall through to it once; writes only ever use STORAGE_KEY.
const LEGACY_KEY = 'postLoginRedirect';

const AUTH_PATHS = ['/login', '/register', '/auth-callback', '/forgot-password', '/reset-password', '/link-account'];

// Role-restricted / former-Student-Portal routes that must NEVER be a
// post-authentication destination for a normal customer. This is the
// central defence against the "customer gets redirected into a portal"
// regression — any stale sessionStorage value, malicious `?redirect=`
// param, or historic returnTo that points at one of these paths is
// silently dropped so the visitor safely lands on `/` instead.
//
// Coach + Admin still access their portals by clicking their explicit
// entry points in the nav bar; they do NOT depend on returnTo.
const PORTAL_PATHS = [
  '/dashboard',           // legacy Student Portal + admin/coach dashboard
  '/admin',               // admin console
  '/coach-portal',        // coach portal
  '/coach-dashboard',     // legacy coach dashboard alias
  '/portal',              // any generic portal alias
  '/student',             // legacy student sidebar
  '/student-dashboard',
  '/students',            // admin subroute
  '/classes',             // admin subroute (class management)
];

const isSafeInternalPath = (raw) => {
  if (!raw || typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;
  // Same-origin, no protocol-relative attacks
  if (!trimmed.startsWith('/')) return false;
  if (trimmed.startsWith('//')) return false;
  const pathname = trimmed.split('?')[0].split('#')[0];
  // Don't return the user to an auth screen — creates redirect loops
  if (AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return false;
  // Don't return normal customers to any dashboard / portal route.
  // Coach & admin roles reach their portals via their own nav entries,
  // not via this returnTo pipeline.
  if (PORTAL_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return false;
  return true;
};

/**
 * Save an explicit path, or the current URL if none provided, as the return
 * target after authentication. Called by every "Sign In" button/link.
 */
export const saveReturnTo = (path) => {
  try {
    const target = path || `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!isSafeInternalPath(target)) return;
    sessionStorage.setItem(STORAGE_KEY, target);
  } catch (_) { /* sessionStorage unavailable — noop */ }
};

/**
 * Read and clear the saved return target. Returns a safe internal path or null.
 * Also drains the legacy `postLoginRedirect` key for a smooth transition.
 */
export const consumeReturnTo = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(LEGACY_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_KEY);
    return isSafeInternalPath(raw) ? raw : null;
  } catch (_) {
    return null;
  }
};

/**
 * Peek without clearing — used by Login.js to render "You'll return to
 * X after signing in" hints if we ever want to.
 */
export const peekReturnTo = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(LEGACY_KEY);
    return isSafeInternalPath(raw) ? raw : null;
  } catch (_) {
    return null;
  }
};

/**
 * Sanitizer exported for callers that receive a path from an untrusted source
 * (e.g. `?redirect=` query param). Returns the trimmed safe path or null.
 */
export const sanitizeReturnTo = (raw) => {
  if (!isSafeInternalPath(raw)) return null;
  return raw.trim();
};

// ─────────────────────────────────────────────────────────────────
// Admin deep-link return-to (separate channel — never mixes with the
// customer pipeline above, which deliberately drops every /admin path).
// Used so an admin who opens e.g. /admin/payments/<id>/verify from an
// email lands back on that exact page after signing in.
// ─────────────────────────────────────────────────────────────────
const ADMIN_STORAGE_KEY = 'adminReturnTo';
const ADMIN_NO_RETURN = ['/admin/change-password', '/admin/access-denied', '/admin/accept-invite'];

const isSafeAdminPath = (raw) => {
  if (!raw || typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/admin/') || trimmed.startsWith('//')) return false;
  const pathname = trimmed.split('?')[0].split('#')[0];
  if (pathname === '/admin/' || pathname === '/admin/login') return false;
  return !ADMIN_NO_RETURN.some((p) => pathname === p || pathname.startsWith(`${p}/`));
};

export const saveAdminReturnTo = (path) => {
  try {
    if (isSafeAdminPath(path)) sessionStorage.setItem(ADMIN_STORAGE_KEY, path.trim());
  } catch (_) { /* noop */ }
};

export const consumeAdminReturnTo = () => {
  try {
    const raw = sessionStorage.getItem(ADMIN_STORAGE_KEY);
    sessionStorage.removeItem(ADMIN_STORAGE_KEY);
    return isSafeAdminPath(raw) ? raw.trim() : null;
  } catch (_) {
    return null;
  }
};

// ─────────────────────────────────────────────────────────────────
// Google OAuth helpers
// ─────────────────────────────────────────────────────────────────
// Oct 2026 — booth-day fix. The documented entry point
// (https://auth.emergentagent.com/?redirect=…) loads a heavy single-page
// hand-off screen that then calls this exact backend URL, which answers with
// a server-side 307 straight to Google. Users on slow mobile data / in-app
// browsers saw that hand-off screen stay blank, so we go to the 307 directly:
// no JavaScript, no intermediate page, identical cookies + state handling.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_OAUTH_ENDPOINT = 'https://demobackend.emergentagent.com/auth/v1/env/oauth';

const IN_APP_UA = /FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Line\/|Twitter|TikTok|BytedanceWebview|Snapchat|MicroMessenger|; wv\)/i;

/** True inside Facebook / Instagram / WhatsApp / TikTok etc. embedded browsers, where Google sign-in is blocked. */
export const isInAppBrowser = () => {
  const ua = navigator.userAgent || '';
  if (IN_APP_UA.test(ua)) return true;
  // iOS WebView: Safari UA without the "Safari/" token.
  return /iPhone|iPad|iPod/i.test(ua) && !/Safari\//i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
};

/**
 * Build the full Google OAuth URL and stash the returnTo BOTH in
 * sessionStorage (fast path) AND as a query parameter on the callback URL
 * (survives sessionStorage loss, private-mode edge cases, and cross-tab
 * roundtrips). AuthCallback.js reads the URL param first, then falls back
 * to sessionStorage, then to Home.
 *
 * @param {string} explicitReturnTo Optional path override. Defaults to the
 *        current URL (pathname + search + hash) so callers can just call
 *        `startGoogleOauth()` from any browsing surface.
 */
export const startGoogleOauth = (explicitReturnTo) => {
  const target =
    explicitReturnTo ||
    peekReturnTo() ||
    `${window.location.pathname}${window.location.search}${window.location.hash}`;

  // In-app browsers (WhatsApp/Instagram/Facebook) can't complete Google
  // sign-in — send the visitor to the login page, which explains the fix.
  if (isInAppBrowser()) {
    saveReturnTo(target);
    window.location.assign(`/login?redirect=${encodeURIComponent(target)}&inapp=1`);
    return;
  }

  // Save to storage as the fast path (same-tab, same-origin).
  saveReturnTo(target);

  // Belt-and-braces: put it on the callback URL too. If the browser drops
  // sessionStorage during the OAuth hop (private mode, storage-partitioning),
  // AuthCallback.js can still recover it from location.search.
  const callback = new URL(`${window.location.origin}/auth-callback`);
  if (target && isSafeInternalPath(target)) {
    callback.searchParams.set('returnTo', target);
  }

  window.location.href = `${GOOGLE_OAUTH_ENDPOINT}?redirect=${encodeURIComponent(callback.toString())}`;
};
